import React from "react";

import { StatsTimeSeriesPointDto } from "services/stats/restModels";
import { Banner, Button, EmptyState, LineChart, LineChartPoint, Modal, Skeleton } from "shared/ui";

import { useStatisticsContext } from "features/Statistics/context";
import { formatPointLabel, spansMultipleDays } from "features/Statistics/lib/chartLabels";
import { StatsMetric } from "features/Statistics/types";

const METRIC_TITLES: { [key in StatsMetric]: string } = {
    revenue: "Выручка",
    orders: "Заказы",
    averageCheck: "Средний чек",
};

const metricValue = (point: StatsTimeSeriesPointDto, metric: StatsMetric): number => {
    if (metric === "orders") return point.ordersCount;

    return metric === "revenue" ? point.revenue : point.averageCheck;
};

export const ChartModal = () => {
    const {
        chartMetric,
        closeChart,
        series,
        seriesLoading,
        seriesError,
        periodLabel,
        interval,
        formatAmount,
    } = useStatisticsContext();

    if (chartMetric === null) return null;

    const title = METRIC_TITLES[chartMetric];

    // Количество заказов — не деньги: форматировать его валютой нельзя.
    const formatValue = (value: number) =>
        chartMetric === "orders" ? String(Math.round(value)) : formatAmount(value, series?.currencyId ?? null);

    const points: LineChartPoint[] = (() => {
        if (!series || series.points.length === 0) return [];

        const first = series.points[0];
        const last = series.points[series.points.length - 1];
        const multiDay = spansMultipleDays(first.from, last.to);

        return series.points.map((point) => ({
            label: formatPointLabel(point.from, interval.period, multiDay),
            value: metricValue(point, chartMetric),
        }));
    })();

    const renderBody = () => {
        if (seriesError) {
            return <Banner tone="danger" title="Не удалось построить график" />;
        }

        /*
        * Пустой ряд до первого ответа — тоже загрузка: запрос стартует эффектом хука, то есть
        * кадром позже открытия модалки, и по одному seriesLoading она успевала мигнуть
        * «Нет данных».
        * */
        if (seriesLoading || series === null) return <Skeleton height="340px" />;

        if (points.length === 0) {
            return (
                <EmptyState
                    title="Нет данных"
                    description="За выбранный период заказов не было — строить график не по чему."
                />
            );
        }

        return <LineChart points={points} seriesLabel={title} formatValue={formatValue} />;
    };

    return (
        <Modal
            open
            onClose={closeChart}
            title={`${title} · ${periodLabel}`}
            maxWidth="md"
            actions={
                <Button variant="text" color="neutral" onClick={closeChart}>
                    Закрыть
                </Button>
            }
        >
            {renderBody()}
        </Modal>
    );
};
